// See: backend/docs/arvento-telemetry.md §20.2
// Günlük gerçek (Arvento) vs planlanan (servis/puantaj) km.
// Kapalı satır = son 30 gün toplamı. Açık satır = o aracın günleri.
// Filtre: plaka veya sürücü, her karakterde. ğ/g ü/u ş/s ö/o ç/c ı/i iki yönde de eşleşir.

import React, { useEffect, useState, useCallback, useMemo } from "react";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Button from "shared/components/FormElements/Button";
import Modal from "shared/components/UIElements/Modal";
import { useHttpClient } from "shared/hooks/http-hook";
import "./DailyKmComparison.css";

const STATUS_LABEL = {
  COMPARED: { text: "Karşılaştırıldı", cls: "dkm-status--compared" },
  SERVICELESS_KM: { text: "Servissiz km", cls: "dkm-status--serviceless" },
  NOT_DRIVEN: { text: "Araç çalışmadı", cls: "dkm-status--notdriven" },
};

function foldTr(value) {
  return String(value || "")
    .toLocaleLowerCase("tr")
    .replace(/ı/g, "i")
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c");
}

function round1(n) {
  return Math.round(n * 10) / 10;
}

function diffFromParts(actualKm, plannedKm, connectionKm) {
  if (actualKm === null || actualKm === undefined || !Number.isFinite(Number(actualKm))) return null;
  const link = connectionKm === null || connectionKm === undefined || !Number.isFinite(Number(connectionKm)) ? 0 : Number(connectionKm);
  return round1(Number(actualKm) - (Number(plannedKm) || 0) - link);
}

function format1(n) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  return Number(n).toLocaleString("tr-TR", {
    maximumFractionDigits: 0,
  });
}

function formatKm(n) {
  const text = format1(n);
  return text === "—" ? text : `${text} km`;
}

function formatDiff(n) {
  const text = format1(n);
  if (text === "—") return text;
  return `${Number(n) > 0 ? "+" : ""}${text} km`;
}

// Tahmin. Araçta yakıt sensörü yok. 13 L/100 km = şehir içi 14 ile şehir dışı 11'in karışımı.
// Motorin: Antalya pompa, Petrol Ofisi, 2 Kasım 2026.
const LITERS_PER_100KM = 13;
const DIESEL_TL_PER_LITER = 102.9;
const DIESEL_PRICE_LABEL = DIESEL_TL_PER_LITER.toLocaleString("tr-TR", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

function fuelTry(km) {
  if (km === null || km === undefined || !Number.isFinite(Number(km))) return null;
  return (Number(km) / 100) * LITERS_PER_100KM * DIESEL_TL_PER_LITER;
}

function formatTry(n, signed) {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return "—";
  const rounded = Math.round(Number(n));
  const text = Math.abs(rounded).toLocaleString("tr-TR");
  if (signed && rounded > 0) return `+${text} TL`;
  if (rounded < 0) return `−${text} TL`;
  return `${text} TL`;
}

function formatDay(ymd) {
  const [y, m, d] = String(ymd || "").split("-");
  if (!y || !m || !d) return ymd || "—";
  return `${d}.${m}.${y}`;
}

function statusOf(actualKm, plannedKm) {
  if (actualKm > 0 && plannedKm > 0) return "COMPARED";
  if (actualKm > 0 && plannedKm === 0) return "SERVICELESS_KM";
  return "NOT_DRIVEN";
}

function connectionTitle(reason, partial) {
  if (reason === "partial" || partial) return "Ev konumu yok. Yalnızca servisler arasındaki yol.";
  if (reason === "geometry") return "Servisin konumu eksik, hesaplanmadı.";
  if (reason === "overlap") return "Servis saatleri çakışıyor, hesaplanmadı.";
  if (reason === "maps") return "Yol mesafesi alınamadı.";
  if (reason === "no_home") return "Tek servis ve ev konumu yok, hesaplanmadı.";
  return "";
}

function diffClass(n) {
  if (n > 0) return "dkm__diff--pos";
  if (n < 0) return "dkm__diff--neg";
  return "";
}

function KmHelpDialog({ open, onClose }) {
  return (
    <Modal
      show={open}
      onCancel={onClose}
      onClose={onClose}
      contentClass="dkm-help-modal__content"
      header="Kilometre karşılaştırması"
      footer={<Button onClick={onClose}>Kapat</Button>}
    >
      <div className="dkm-help__body">
        <p>Bu sayfa dünle biten son 30 günü gösterir. Bugün henüz kapanmadığı için listede yoktur. Gün, Türkiye saatiyle gece yarısında başlar ve ertesi gece yarısında biter. Kapalı satır bu 30 günün toplamıdır. Satır açılınca her gün ayrı görünür.</p>
        <h3>Gerçek km</h3>
        <p>Aracın o gün sayaçta ilerlediği yoldur. Arvento günün başındaki ve sonundaki sayacı verir. İkisinin farkı, aracın gerçekten gittiği kilometredir.</p>
        <h3>Planlanan km</h3>
        <p>Puantajdaki servis yoludur. Toplamada ilk öğrenciden okula kadar. Dağıtmada okuldan son öğrenciye kadar. Aynı gün aynı plakadaki servisler toplanır. Servise gidiş ve akşam dönüş bu sayının içinde değildir.</p>
        <h3>Bağlantı km</h3>
        <p>Servisin dışında kalan iş yoludur. Haritadan hesaplanır, sayaç değildir.</p>
        <ul>
          <li>Sürücünün adresinden ilk servisin başladığı yere.</li>
          <li>Bir servisin bittiği yerden sonrakinin başladığı yere.</li>
          <li>Son servisten sürücünün adresine.</li>
        </ul>
        <p>İki servisin arası üç saatten uzunsa ve adres kayıtlıysa, sürücü o arada bu adrese gidip oradan sonraki servise çıkmış sayılır. Kısa arada yol, bir servisten diğerine doğrudan yazılır.</p>
        <h3>Sürücü adresi</h3>
        <p>Adres, aracın akşam beklediği yer olarak girilmelidir. Hesap sürücünün kartındaki konumu kullanır. Adres boşsa veya yanlışsa bağlantı eksik çıkar: evden ilk servise gidiş ve son servisten dönüş yazılmaz. Bazen yalnız servisler arası kısa yol görünür, bazen o günün bağlantısı boş kalır. Adreslerin doğru olduğundan emin olun.</p>
        <h3>Fark</h3>
        <p>Gerçek kilometreden planlanan kilometre ve bağlantı çıkarılır. Bağlantı hesaplanamadıysa yalnız planlanan çıkarılır. Kalan artı sayı, sayaçta olup bu iki kalemin açıklayamadığı yoldur.</p>
        <h3>Tahmini yakıt</h3>
        <p>Üstteki tutarlar ölçülmüş litre değildir. Araçta yakıt sensörü olmadığı için her kilometre 13 litre / 100 km sayılır. Tutar, bu litrenin Antalya motorin fiyatıyla çarpımıdır. Fiyat kartın üzerinde yazar ve pompa değiştikçe güncellenir.</p>
        <h3>Durum</h3>
        <p>Karşılaştırıldı: hem gerçek kilometre hem planlanan servis vardır. Servissiz km: araç gitmiştir, o gün planlı servis yoktur. Araç çalışmadı: servis planlıdır, sayaç ilerlemiş değildir.</p>
      </div>
    </Modal>
  );
}

function buildVehicles(rows) {
  const map = new Map();
  for (const row of rows) {
    const key = row.vehicleId || row.plate || "—";
    let vehicle = map.get(key);
    if (!vehicle) {
      vehicle = {
        key,
        vehicleId: row.vehicleId,
        plate: row.plate || "—",
        driverName: row.driverName || "",
        days: [],
      };
      map.set(key, vehicle);
    }
    if (!vehicle.driverName && row.driverName) vehicle.driverName = row.driverName;
    if (vehicle.plate === "—" && row.plate) vehicle.plate = row.plate;
    vehicle.days.push(row);
  }

  const vehicles = [...map.values()];
  for (const vehicle of vehicles) {
    vehicle.days.sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
    const chronological = [...vehicle.days].sort((a, b) => (a.day < b.day ? -1 : 1));
    const firstCounter = chronological.find((d) => d.odometerStart !== null && d.odometerStart !== undefined);
    const lastCounter = [...chronological].reverse().find((d) => d.odometerEnd !== null && d.odometerEnd !== undefined);
    vehicle.odometerStart = firstCounter ? firstCounter.odometerStart : null;
    vehicle.odometerEnd = lastCounter ? lastCounter.odometerEnd : null;
    const actualDays = vehicle.days.filter((d) => d.actualKm !== null && d.actualKm !== undefined);
    const actualKm = actualDays.length ? round1(actualDays.reduce((sum, d) => sum + d.actualKm, 0)) : null;
    const plannedKm = round1(vehicle.days.reduce((sum, d) => sum + (d.plannedKm || 0), 0));
    const connectionDays = vehicle.days.filter((d) => d.connectionKm !== null && d.connectionKm !== undefined);
    const connectionKm = connectionDays.length ? round1(connectionDays.reduce((sum, d) => sum + d.connectionKm, 0)) : null;
    const connectionPartial = vehicle.days.some(
      (d) => d.connectionPartial || (d.plannedKm > 0 && (d.connectionKm === null || d.connectionKm === undefined) && d.connectionReason && d.connectionReason !== "no_services"),
    );
    vehicle.actualKm = actualKm;
    vehicle.plannedKm = plannedKm;
    vehicle.connectionKm = connectionKm;
    vehicle.connectionPartial = connectionPartial;
    vehicle.days.forEach((day) => {
      day.diffKm = diffFromParts(day.actualKm, day.plannedKm, day.connectionKm);
    });
    vehicle.diffKm = diffFromParts(actualKm, plannedKm, connectionKm);
    vehicle.status = statusOf(actualKm || 0, plannedKm);
  }
  vehicles.sort((a, b) => String(a.plate).localeCompare(String(b.plate), "tr"));
  return vehicles;
}

const StatusBadge = ({ status }) => {
  const st = STATUS_LABEL[status] || { text: status, cls: "" };
  return <span className={`dkm-status ${st.cls}`}>{st.text}</span>;
};

const DailyKmComparison = ({ from, to }) => {
  const { sendRequest } = useHttpClient();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const [sort, setSort] = useState({ key: "diffKm", dir: "desc" });
  const [helpOpen, setHelpOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const qs = new URLSearchParams();
      if (from) qs.set("from", from);
      if (to) qs.set("to", to);
      const data = await sendRequest(`${process.env.REACT_APP_BACKEND_URL}/telemetry/km-comparison?${qs.toString()}`);
      setRows(Array.isArray(data?.rows) ? data.rows : []);
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [sendRequest, from, to]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const vehicles = useMemo(() => buildVehicles(rows), [rows]);

  const fleetTotal = useMemo(() => {
    let actual = 0;
    let hasActual = false;
    let planned = 0;
    let connection = 0;
    let hasConnection = false;
    for (const vehicle of vehicles) {
      if (vehicle.actualKm !== null && vehicle.actualKm !== undefined) {
        actual += vehicle.actualKm;
        hasActual = true;
      }
      planned += vehicle.plannedKm || 0;
      if (vehicle.connectionKm !== null && vehicle.connectionKm !== undefined) {
        connection += vehicle.connectionKm;
        hasConnection = true;
      }
    }
    const actualKm = hasActual ? round1(actual) : null;
    const plannedKm = round1(planned) || 0;
    return {
      actualKm,
      plannedKm,
      connectionKm: hasConnection ? round1(connection) : null,
      diffKm: diffFromParts(actualKm, plannedKm, hasConnection ? connection : null),
    };
  }, [vehicles]);

  const filtered = useMemo(() => {
    const q = foldTr(query).trim();
    if (!q) return vehicles;
    const qPlate = q.replace(/\s+/g, "");
    return vehicles.filter((v) => {
      const plate = foldTr(v.plate).replace(/\s+/g, "");
      const name = foldTr(v.driverName);
      return plate.includes(qPlate) || name.includes(q);
    });
  }, [vehicles, query]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const { key, dir } = sort;
    const mul = dir === "asc" ? 1 : -1;
    const byPlate = (a, b) => String(a.plate || "").localeCompare(String(b.plate || ""), "tr");
    arr.sort((a, b) => {
      if (key === "plate") {
        const plate = byPlate(a, b);
        if (plate !== 0) return mul * plate;
        return String(a.driverName || "").localeCompare(String(b.driverName || ""), "tr");
      }
      if (key === "status") {
        const la = (STATUS_LABEL[a.status] && STATUS_LABEL[a.status].text) || "";
        const lb = (STATUS_LABEL[b.status] && STATUS_LABEL[b.status].text) || "";
        const cmp = la.localeCompare(lb, "tr");
        return cmp !== 0 ? mul * cmp : byPlate(a, b);
      }
      const av = a[key];
      const bv = b[key];
      const aMissing = av === null || av === undefined;
      const bMissing = bv === null || bv === undefined;
      if (aMissing && bMissing) return byPlate(a, b);
      if (aMissing) return 1;
      if (bMissing) return -1;
      if (av === bv) return byPlate(a, b);
      return av < bv ? -mul : mul;
    });
    return arr;
  }, [filtered, sort]);

  const toggleSort = (key) => {
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: key === "plate" || key === "status" ? "asc" : "desc" },
    );
  };

  const toggle = (key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  if (loading && rows.length === 0) {
    return (
      <div className="dkm" aria-busy="true" aria-label="Yükleniyor">
        <div className="dkm-skel-table">
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="dkm-skel-row">
              <div className="dkm-skel-cell dkm-skel-cell--date" />
              <div className="dkm-skel-cell dkm-skel-cell--plate" />
              <div className="dkm-skel-cell" />
              <div className="dkm-skel-cell" />
              <div className="dkm-skel-cell" />
              <div className="dkm-skel-cell dkm-skel-cell--status" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="dkm">
      <section className="dkm-board" aria-label="Tüm araçların toplamı">
        <div className="dkm-board__head">
          <div>
            <p className="dkm-board__kicker">Tüm araçlar</p>
            <h2 className="dkm-board__title">Son 30 gün</h2>
            <p className="dkm-board__range">{from && to ? `${formatDay(from)} – ${formatDay(to)}` : ""}</p>
          </div>
          <p className="dkm-board__note">
            Tahmini yakıt
            <strong>{LITERS_PER_100KM} L/100 km</strong>
            <span>Antalya motorin {DIESEL_PRICE_LABEL} TL/L · 2 Kasım 2026</span>
          </p>
        </div>
        <div className="dkm-board__grid">
          {[
            { label: "Gerçek", km: fleetTotal.actualKm, accent: "actual", hint: "Sayaç farkı." },
            { label: "Planlanan", km: fleetTotal.plannedKm, accent: "planned", hint: "Puantaj servis yolu." },
            { label: "Bağlantı", km: fleetTotal.connectionKm, accent: "link", hint: "Ev, servisler arası ve dönüş. Harita mesafesi." },
            { label: "Fark", km: fleetTotal.diffKm, accent: "diff", signed: true, hint: "Gerçek − planlanan − bağlantı. Bağlantı boşsa yalnız planlanan çıkar." },
          ].map((card) => (
            <article key={card.label} className={`dkm-card dkm-card--${card.accent}`} title={card.hint}>
              <p className="dkm-card__label">{card.label}</p>
              <p className={`dkm-card__km ${card.signed ? diffClass(card.km) : ""}`}>
                {card.signed ? formatDiff(card.km) : formatKm(card.km)}
              </p>
              <p className={`dkm-card__tl ${card.signed ? diffClass(card.km) : ""}`}>{formatTry(fuelTry(card.km), card.signed)}</p>
            </article>
          ))}
        </div>
      </section>
      <div className="dkm__toolbar">
        <div className="dkm__lead">
          <input
            className="dkm__filter"
            type="search"
            value={query}
            placeholder="Plaka veya sürücü adı"
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Plaka veya sürücü ara"
          />
        </div>
        <Button danger size="small" onClick={() => setHelpOpen(true)}>
          Yardım
        </Button>
      </div>
      {filtered.length === 0 ? (
        <div className="dkm__empty">{rows.length === 0 ? "Bu aralık için km verisi yok." : "Eşleşen araç yok."}</div>
      ) : (
        <table className="dkm__table">
          <thead>
            <tr>
              <th className="dkm__col-toggle"></th>
              {["plate", "odometerStart", "odometerEnd", "actualKm", "plannedKm", "connectionKm", "diffKm", "status"].map((key) => {
                const label = {
                  plate: "Plaka",
                  odometerStart: "Başlangıç sayacı",
                  odometerEnd: "Bitiş sayacı",
                  actualKm: "Gerçek km (Arvento)",
                  plannedKm: "Planlanan km (puantaj)",
                  connectionKm: "Bağlantı km",
                  diffKm: "Fark",
                  status: "Durum",
                }[key];
                return (
                  <th
                    key={key}
                    className="dkm__th-sort"
                    onClick={() => toggleSort(key)}
                    title={key === "diffKm" ? "Gerçek km − planlanan km − bağlantı km. Bağlantı boşsa yalnız planlanan çıkar." : "Sıralamak için tıkla"}
                  >
                    {label}
                    <span className="dkm__sort-ind">
                      {sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {sorted.map((v) => {
              const isOpen = openKeys.has(v.key);
              return (
                <React.Fragment key={v.key}>
                  <tr className={`dkm__vrow ${isOpen ? "dkm__vrow--open" : ""}`} onClick={() => toggle(v.key)}>
                    <td className="dkm__col-toggle">
                      <ChevronRightIcon className={`dkm__chevron ${isOpen ? "dkm__chevron--open" : ""}`} fontSize="small" />
                    </td>
                    <td>
                      <span className="dkm__plate">{v.plate}</span>
                      {v.driverName ? <span className="dkm__driver">{v.driverName}</span> : null}
                    </td>
                    <td>{format1(v.odometerStart)}</td>
                    <td>{format1(v.odometerEnd)}</td>
                    <td>{formatKm(v.actualKm)}</td>
                    <td>{formatKm(v.plannedKm)}</td>
                    <td title={v.connectionPartial ? connectionTitle("partial") : ""}>{formatKm(v.connectionKm)}</td>
                    <td className={diffClass(v.diffKm)}>{formatDiff(v.diffKm)}</td>
                    <td>
                      <StatusBadge status={v.status} />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="dkm__detail-row">
                      <td colSpan={9}>
                        <div className="dkm__days">
                          <table className="dkm__table dkm__day-table">
                            <thead>
                              <tr>
                                <th>Tarih</th>
                                <th>Başlangıç sayacı</th>
                                <th>Bitiş sayacı</th>
                                <th>Gerçek km (Arvento)</th>
                                <th>Planlanan km (puantaj)</th>
                                <th>Bağlantı km</th>
                                <th>Fark</th>
                                <th>Durum</th>
                              </tr>
                            </thead>
                            <tbody>
                              {v.days.map((d) => (
                                <tr key={`${v.key}-${d.day}`}>
                                  <td>{formatDay(d.day)}</td>
                                  <td>{format1(d.odometerStart)}</td>
                                  <td>{format1(d.odometerEnd)}</td>
                                  <td>{formatKm(d.actualKm)}</td>
                                  <td>{formatKm(d.plannedKm)}</td>
                                  <td title={connectionTitle(d.connectionReason, d.connectionPartial)}>{formatKm(d.connectionKm)}</td>
                                  <td className={diffClass(d.diffKm)}>{formatDiff(d.diffKm)}</td>
                                  <td>
                                    <StatusBadge status={d.status} />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      )}
      <KmHelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
};

export default DailyKmComparison;
